import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ExternalLink, Plus, Trash2, AlertTriangle, Boxes } from "lucide-react";
import { Button, Spinner, Field } from "../components/ui/kit";
import { ZaraditDialog } from "../components/SkladDialogy";
import { fmtKc } from "../lib/format";
import api, { apiError } from "../lib/api";
import { Zpet, useNazevStranky } from "../lib/navigace";
import Vyber from "../components/Vyber";
import { Duplicita, PrirazeniDialog } from "../components/FakturyPripadu";

/** Číslo zakázky si nikdo nepamatuje — v selectu musí být i název a klient. */
function popisPripadu(p) {
  const zaklad = p.cislo ? `${p.cislo} · ${p.nazev}` : p.nazev;
  return p.client_name ? `${zaklad} — ${p.client_name}` : zaklad;
}

const UHRADA = {
  UHRAZENO: { label: "Uhrazeno", cls: "bg-success-bg text-success" },
  CASTECNE: { label: "Částečně", cls: "bg-accent/10 text-accent" },
  NEUHRAZENO: { label: "Neuhrazeno", cls: "bg-inputbg text-sec" },
  NEPLATI_SE: { label: "Neplatí se", cls: "bg-white/10 text-white/70" },
};

const DRUHY = [
  ["FAKTURA", "Faktura"], ["ZALOHOVA", "Zálohová faktura"], ["DANOVY_DOKLAD", "Daňový doklad k platbě"],
  ["DOBROPIS", "Dobropis"], ["DODACI_LIST", "Dodací list"],
];
/** Co druh dokladu znamená pro peníze — jedna věta pod výběrem. */
const DOPAD = {
  ZALOHOVA: "Záloha není náklad. Vydaná se počítá do tržeb tím, co klient zaplatil.",
  DANOVY_DOKLAD: "Potvrzuje zaplacenou zálohu — sám se neplatí ani nepočítá.",
  DOBROPIS: "Snižuje náklad nebo tržbu o svou částku.",
  DODACI_LIST: "Není náklad ani se neplatí — náklad přijde s fakturou.",
};

/** Kam vydaná faktura patří, slovy: technologie (a podíly), nebo jen případ. */
function patriK(d, pripady) {
  const p = pripady.find((x) => x.id === d.obchodni_pripad_id);
  if (!p) return "Zatím k ničemu — tržba se nepočítá do žádné zakázky.";
  const nazev = (tid) => (p.technologie.find((t) => t.id === tid) || {}).nazev || "Technologie";
  const kde = d.rozdeleni?.length
    ? d.rozdeleni.map((r) => `${nazev(r.technologie_id)} ${Math.round(r.podil * 100)} %`).join(", ")
    : d.technologie_id ? nazev(d.technologie_id) : "technologie nevybraná";
  return `${popisPripadu(p)} · ${kde}`;
}

function groupByDL(polozky) {
  const g = {};
  for (const p of polozky) {
    const k = p.dodaci_list || "__";
    (g[k] = g[k] || []).push(p);
  }
  return g;
}

export default function DokladDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(undefined);
  const [chybaSmazani, setChybaSmazani] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [platba, setPlatba] = useState({ datum: new Date().toISOString().slice(0, 10), castka: "", typ: "BANKA" });
  const [sklady, setSklady] = useState([]);
  const [naSklad, setNaSklad] = useState(false);
  const [prirazeniPripad, setPrirazeniPripad] = useState(null);
  const backend = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/api/doklady/${id}`); setD(data); }
    catch { setD(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useNazevStranky(d && `Doklad ${d.cislo || ""}`.trim());
  useEffect(() => {
    api.get("/api/sklady").then(({ data }) => setSklady(data.items || [])).catch(() => setSklady([]));
  }, []);

  if (d === undefined) return <div className="p-10 flex justify-center"><Spinner /></div>;
  if (d === null) return <div className="card p-8 text-center text-muted">Doklad nenalezen.</div>;

  const setSmer = async (smer) => { await api.patch(`/api/doklady/${id}`, { smer }); load(); };
  const setDruh = async (druh) => {
    setErr("");
    try { await api.patch(`/api/doklady/${id}`, { druh }); load(); }
    catch (e) { setErr(apiError(e.response?.data?.detail)); }
  };
  const patchField = async (field, value) => { await api.patch(`/api/doklady/${id}`, { [field]: value }); };
  const assignGroup = async (polozka_ids, obchodni_pripad_id) => {
    await api.post(`/api/doklady/${id}/rozdelit`, { polozka_ids, obchodni_pripad_id: obchodni_pripad_id || null });
    load();
  };
  const addPlatba = async () => {
    if (!platba.castka) return;
    setSaving(true); setErr("");
    try {
      await api.post(`/api/doklady/${id}/platby`, { ...platba, castka: Number(platba.castka) });
      setPlatba({ ...platba, castka: "" }); load();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const delPlatba = async (pid) => { await api.delete(`/api/platby/${pid}`); load(); };
  const groups = groupByDL(d.polozky || []);
  const pripady = d.pripady_list || [];
  const stav = UHRADA[d.stav_uhrady] || UHRADA.NEUHRAZENO;

  // Doplatek: haléřová tolerance jako u `_stav_uhrady` na backendu, ať se
  // stav „uhrazeno" a nulový doplatek nikdy nerozejdou.
  const celkem = d.k_uhrade ?? (d.castka_s_dph || 0);
  const neplatiSe = d.stav_uhrady === "NEPLATI_SE";
  const zaplaceno = d.zaplaceno || 0;
  const zbyva = d.zbyva_uhradit ?? (celkem - zaplaceno);
  const preplatek = zbyva < -0.5;
  const uhrazeno = !preplatek && zbyva <= 0.5;
  const podilZaplaceno = celkem > 0 ? Math.min(100, Math.round((zaplaceno / celkem) * 100)) : 0;

  const smazatDoklad = async () => {
    if (!window.confirm("Opravdu smazat tento doklad? Zmizí z ekonomiky i z přehledu úhrad.")) return;
    setChybaSmazani("");
    try {
      await api.delete(`/api/doklady/${id}`);
      nav("/doklady");
    } catch (e) {
      setChybaSmazani(apiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="doklad-detail">
      <div className="flex items-center gap-3 mb-4">
        <Zpet nahradni="/doklady" nahradniNazev="Doklady" />
        <button onClick={smazatDoklad} data-testid="doklad-smazat"
          className="tap ml-auto inline-flex items-center gap-1.5 t-meta font-700 text-danger hover:underline">
          <Trash2 size={14} /> Smazat doklad
        </button>
      </div>
      {chybaSmazani && <div className="card px-5 py-3 mb-4 t-meta text-danger" data-testid="doklad-smazat-chyba">{chybaSmazani}</div>}

      {d.ocr_stav === "ZPRACOVAVA_SE" && (
        <div className="card px-5 py-4 mb-4 text-[13px] text-accent" data-testid="doklad-processing">🤖 Čtu doklad…</div>
      )}

      {/* směr nerozpoznán */}
      {!d.smer && (
        <div className="card px-5 py-4 mb-4 border-accent/30 bg-accent/[0.05]" data-testid="smer-doptani">
          <div className="flex items-center gap-2 text-[13px] font-600 text-ink mb-2">
            <AlertTriangle size={16} className="text-accent" /> Nepoznali jsme, zda je doklad přijatý nebo vydaný.
          </div>
          <div className="text-[12px] text-muted mb-3">Rozhodujeme podle IČO. Doplň IČO firmy v Nastavení, ať to poznáme automaticky, nebo vyber ručně:</div>
          <div className="flex gap-2">
            <Button onClick={() => setSmer("PRIJATA")} data-testid="set-smer-prijata">Přijatá (my platíme)</Button>
            <Button variant="ghost" onClick={() => setSmer("VYDANA")} data-testid="set-smer-vydana">Vydaná (nám platí)</Button>
          </div>
        </div>
      )}

      <div className="card p-6 mb-4">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {d.smer && <span className="label-up px-1.5 py-0.5 rounded-chip bg-inputbg text-sec">{d.smer === "PRIJATA" ? "Přijatá" : "Vydaná"}</span>}
              {d.smer_jistota === "NAVRH" && <span className="label-up px-1.5 py-0.5 rounded-chip bg-accent/10 text-accent">směr jen návrh — zkontroluj</span>}
              <span className="label-up px-1.5 py-0.5 rounded-chip bg-inputbg text-muted">{d.konecna ? "Konečná faktura" : d.druh_nazev}</span>
            </div>
            <h1 className="text-[20px] font-800 text-ink">{(d.protistrana || {}).nazev || "Protistrana"}</h1>
            <div className="text-[12px] text-muted mt-0.5">{d.cislo || "bez čísla"} {d.variabilni_symbol ? `· VS ${d.variabilni_symbol}` : ""}</div>
          </div>
          {/* Doklad může mít víc stran (vyfocený dodací list) — odkaz na každou. */}
          {(d.soubory_urls || []).length > 1 ? (
            <div className="flex gap-2 shrink-0 flex-wrap justify-end">
              {d.soubory_urls.map((s, i) => (
                <a key={i} href={`${backend}${s.url}`} target="_blank" rel="noreferrer"
                  className="btn-ghost" data-testid={`doklad-soubor-link-${i}`}>
                  Strana {i + 1} <ExternalLink size={14} />
                </a>
              ))}
            </div>
          ) : d.soubor_url && (
            <a href={`${backend}${d.soubor_url}`} target="_blank" rel="noreferrer"
              className="btn-ghost shrink-0" data-testid="doklad-soubor-link">
              Originál <ExternalLink size={14} />
            </a>
          )}
        </div>

        {/* editovatelná hlavička */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="col-span-2 md:col-span-1">
            <span className="label-up text-muted block mb-1.5">Druh dokladu</span>
            <Vyber className="field" value={d.druh} onChange={(e) => setDruh(e.target.value)} data-testid="f-druh">
              {DRUHY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Vyber>
            {DOPAD[d.druh] && <div className="t-meta text-muted mt-1">{DOPAD[d.druh]}</div>}
          </div>
          <Field label="Číslo dokladu" inputProps={{ defaultValue: d.cislo || "", onBlur: (e) => patchField("cislo", e.target.value), "data-testid": "f-cislo" }} />
          <Field label="Variabilní symbol" inputProps={{ defaultValue: d.variabilni_symbol || "", onBlur: (e) => patchField("variabilni_symbol", e.target.value) }} />
          <Field label="Datum vystavení" inputProps={{ type: "date", defaultValue: d.datum_vystaveni || "", onBlur: (e) => patchField("datum_vystaveni", e.target.value) }} />
          <Field label="Splatnost" inputProps={{ type: "date", defaultValue: d.datum_splatnosti || "", onBlur: (e) => patchField("datum_splatnosti", e.target.value), "data-testid": "f-splatnost" }} />
          {/* U konečné faktury se zadává celé plnění — doplatek po zálohách se přepočte sám. */}
          <Field label={d.celkova_castka_s_dph != null ? "Celkem s DPH (před zálohami)" : "Částka s DPH"}
            inputProps={{ type: "number", defaultValue: d.celkova_castka_s_dph ?? d.castka_s_dph ?? 0, onBlur: (e) => patchField("castka_s_dph", Number(e.target.value)).then(load), "data-testid": "f-castka" }} />
          <Field label={d.celkova_castka_s_dph != null ? "Základ bez DPH (před zálohami)" : "Základ bez DPH"}
            inputProps={{ type: "number", defaultValue: d.celkova_castka_bez_dph ?? d.castka_bez_dph ?? 0, onBlur: (e) => patchField("castka_bez_dph", Number(e.target.value)).then(load) }} />
        </div>
      </div>

      {d.duplicita_id && (
        <div className="card overflow-hidden mb-4">
          <Duplicita d={d} onZmena={load} onOtevrit={(fid) => nav(`/doklady/${fid}`)} />
        </div>
      )}

      {/* Vydaná faktura patří k technologii případu (tržba se počítá z hlavičky), nebo se rozdělí na víc. */}
      {d.smer === "VYDANA" && (
        <div className="card px-5 py-4 mb-4 flex flex-col md:flex-row md:items-center gap-3" data-testid="vydana-patri">
          <div className="min-w-0 flex-1">
            <div className="label-up text-muted">Patří k</div>
            <div className="t-telo text-ink mt-1">{patriK(d, pripady)}</div>
          </div>
          <Vyber className="field md:w-[280px]" value="" data-testid="vydana-pripad"
            onChange={(e) => e.target.value && setPrirazeniPripad(e.target.value)}>
            <option value="">{d.obchodni_pripad_id ? "Změnit…" : "Vyber obchodní případ…"}</option>
            {d.obchodni_pripad_id && <option value={d.obchodni_pripad_id}>Jiná technologie nebo rozdělení</option>}
            {pripady.filter((p) => p.id !== d.obchodni_pripad_id).map((p) => <option key={p.id} value={p.id}>{popisPripadu(p)}</option>)}
          </Vyber>
        </div>
      )}
      {prirazeniPripad && (
        <PrirazeniDialog doklad={d} pripadId={prirazeniPripad} onClose={() => setPrirazeniPripad(null)} onHotovo={load} />
      )}

      {d.zuctovana_fakturou_id && (
        <div className="card px-5 py-4 mb-4 flex items-center gap-3" data-testid="zaloha-zuctovana">
          <span className="h-2 w-2 rounded-full bg-success shrink-0" />
          <span className="text-[13px] text-sec flex-1">Tato záloha je zúčtovaná konečnou fakturou.</span>
          <button onClick={() => nav(`/doklady/${d.zuctovana_fakturou_id}`)} className="text-[12px] font-600 text-accent">Otevřít →</button>
        </div>
      )}
      {d.danovy_doklad && (
        <div className="card px-5 py-4 mb-4 flex items-center gap-3" data-testid="zaloha-danovy-doklad">
          <span className="h-2 w-2 rounded-full bg-success shrink-0" />
          <span className="t-meta text-sec flex-1">Platbu zálohy dokládá daňový doklad {d.danovy_doklad.cislo || ""}.</span>
          <button onClick={() => nav(`/doklady/${d.danovy_doklad.id}`)} className="t-meta font-600 text-accent">Otevřít →</button>
        </div>
      )}
      {d.odectene_zalohy_kc > 0 && (
        <div className="card px-5 py-4 mb-4 text-[13px] text-sec" data-testid="kf-souhrn">
          Celková cena <span className="amount font-700 text-ink">{fmtKc(d.celkova_castka_s_dph || 0)} Kč</span> ·
          odečteno záloh <span className="amount font-700 text-ink">{fmtKc(d.odectene_zalohy_kc)} Kč</span> ·
          k úhradě <span className="amount font-700 text-ink">{fmtKc(d.castka_s_dph || 0)} Kč</span>
        </div>
      )}

      {/* položky seskupené po dodacích listech */}
      {(d.polozky || []).length > 0 && (
        <div className="card overflow-hidden mb-4" data-testid="doklad-polozky">
          <div className="px-5 py-4 border-b border-line-softer flex items-center justify-between">
            <h3 className="text-[14.5px] font-700 text-ink">Položky ({d.polozky.length})</h3>
            {d.pocet_dodacich_listu > 0 && <span className="text-[12px] text-muted">{d.pocet_dodacich_listu} dodacích listů</span>}
            {/* Materiál, který nejde rovnou na zakázku, patří na sklad. */}
            {d.smer === "PRIJATA" && sklady.length > 0 && (
              <button className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-700 text-accent-hover"
                onClick={() => setNaSklad(true)} data-testid="doklad-na-sklad">
                <Boxes size={14} /> Zařadit na sklad
              </button>
            )}
          </div>
          {Object.entries(groups).map(([dl, rows]) => (
            <div key={dl} className="border-b border-line-softer last:border-0">
              {dl !== "__" && d.smer !== "VYDANA" && (
                <div className="px-5 py-2.5 bg-inputbg flex items-center gap-3 flex-wrap" data-testid={`dl-group-${dl}`}>
                  <span className="text-[12px] font-700 text-ink">Dodací list {dl}</span>
                  {rows[0].akce && <span className="text-[12px] text-muted">· akce: {rows[0].akce}</span>}
                  <select className="field h-8 text-[12px] ml-auto w-[220px]"
                    defaultValue={rows[0].obchodni_pripad_id || ""}
                    onChange={(e) => assignGroup(rows.map((r) => r.id), e.target.value)}
                    data-testid={`assign-group-${dl}`}>
                    <option value="">— přiřaď na zakázku —</option>
                    {pripady.map((p) => <option key={p.id} value={p.id}>{popisPripadu(p)}</option>)}
                  </select>
                </div>
              )}
              {rows.map((r) => (
                <div key={r.id} className="px-5 py-2.5 flex items-center gap-3 text-[12.5px]">
                  {r.cena_ke_kontrole && <span title="Cena × množství nesedí s celkem — zkontroluj" className="h-2 w-2 rounded-full bg-accent shrink-0" data-testid="cena-kontrola-dot" />}
                  <span className="min-w-0 flex-1 truncate text-ink">{r.nazev}</span>
                  <span className="amount text-muted w-[70px] text-right">{r.mnozstvi} {r.mj}</span>
                  <span className="amount text-muted w-[100px] text-right">{fmtKc(r.cena_za_mj)} Kč</span>
                  <span className="amount font-700 text-ink w-[110px] text-right">{fmtKc(r.celkem)} Kč</span>
                  {d.smer !== "VYDANA" && (<>
                  <Vyber className="field h-8 text-[12px] w-[230px] shrink-0" value={r.obchodni_pripad_id || ""}
                    onChange={async (e) => {
                      await api.patch(`/api/faktura-polozky/${r.id}`,
                        { obchodni_pripad_id: e.target.value || null, technologie_id: null });
                      load();
                    }}
                    data-testid={`assign-row-${r.id}`}>
                    <option value="">— zakázka —</option>
                    {pripady.map((p) => <option key={p.id} value={p.id}>{popisPripadu(p)}</option>)}
                  </Vyber>
                  {/* Bez technologie se náklad do její marže nezapočítá. */}
                  {r.obchodni_pripad_id && (
                    <Vyber className={`field h-8 text-[12px] w-[170px] shrink-0 ${r.technologie_id ? "" : "border-accent"}`}
                      value={r.technologie_id || ""}
                      onChange={async (e) => { await api.patch(`/api/faktura-polozky/${r.id}`, { technologie_id: e.target.value || null }); load(); }}
                      data-testid={`assign-tech-${r.id}`}>
                      <option value="">— technologie —</option>
                      {((pripady.find((p) => p.id === r.obchodni_pripad_id) || {}).technologie || [])
                        .map((t) => <option key={t.id} value={t.id}>{t.nazev}{t.vyrobce ? ` · ${t.vyrobce}` : ""}</option>)}
                    </Vyber>
                  )}
                  </>)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {naSklad && (
        <ZaraditDialog sklady={sklady}
          doklad={{ faktura_id: d.id, cislo: d.cislo, radky: d.polozky || [] }}
          onClose={() => setNaSklad(false)} onSaved={load} />
      )}

      {/* Za dodací list ani daňový doklad k platbě se neplatí. */}
      {neplatiSe ? (
        <div className="card px-5 py-4 mb-4 flex items-center gap-3" data-testid="neplati-se">
          <span className="h-2 w-2 rounded-full bg-muted shrink-0" />
          <span className="t-meta text-sec flex-1">{DOPAD[d.druh]}</span>
          {d.k_zaloze && (
            <button onClick={() => nav(`/doklady/${d.k_zaloze.id}`)} className="t-meta font-600 text-accent">
              Záloha {d.k_zaloze.cislo || ""} →
            </button>
          )}
        </div>
      ) : (<>
      {/* platby / stav úhrady — doplatek je to hlavní číslo, ne celková částka */}
      <div className="rounded-card bg-ink text-white p-5 mb-4" data-testid="uhrada-blok">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="label-up text-white/50 mb-1">Celkem k úhradě</div>
            <div className="t-cislo-velke font-800">{fmtKc(celkem)} Kč</div>
          </div>
          <span className={`label-up px-2.5 py-1 rounded-pill shrink-0 ${stav.cls}`} data-testid="stav-uhrady">{stav.label}</span>
        </div>

        {zaplaceno > 0 && (
          <div className="h-1.5 rounded-pill bg-white/12 mt-4 overflow-hidden" data-testid="uhrada-pas">
            <div className={`h-full rounded-pill ${uhrazeno ? "bg-success" : "bg-accent"}`}
              style={{ width: `${podilZaplaceno}%` }} />
          </div>
        )}

        <div className="flex items-end justify-between gap-4 mt-3">
          <div className="min-w-0">
            <div className="label-up text-white/50">Zaplaceno</div>
            <div className="amount t-telo font-700 text-white/85 mt-0.5" data-testid="uhrada-zaplaceno">
              {fmtKc(zaplaceno)} Kč
            </div>
          </div>
          <div className="text-right min-w-0">
            <div className="label-up text-white/50">
              {preplatek ? "Přeplatek" : uhrazeno ? "Doplatek" : "Zbývá doplatit"}
            </div>
            <div className={`t-cislo-velke font-800 mt-0.5 ${uhrazeno ? "text-success" : "text-accent"}`}
              data-testid="uhrada-zbyva">
              {fmtKc(Math.abs(zbyva))} Kč
            </div>
          </div>
        </div>
      </div>

      <div className="card p-5">
        <h3 className="text-[14.5px] font-700 text-ink mb-3">Platby</h3>
        {(d.platby || []).map((p) => (
          <div key={p.id} className="flex items-center gap-3 py-2 border-b border-line-softer last:border-0 text-[13px]">
            <span className="text-muted w-[110px] amount">{p.datum}</span>
            <span className="label-up text-muted">{p.typ}</span>
            <span className="amount font-700 text-ink ml-auto">{fmtKc(p.castka)} Kč</span>
            <button onClick={() => delPlatba(p.id)} className="text-muted hover:text-danger" data-testid={`del-platba-${p.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        <div className="flex items-end gap-2 mt-3 flex-wrap">
          <Field label="Datum" className="w-[150px]" inputProps={{ type: "date", value: platba.datum, onChange: (e) => setPlatba({ ...platba, datum: e.target.value }) }} />
          <Field label="Částka" className="w-[140px]" inputProps={{ type: "number", value: platba.castka, onChange: (e) => setPlatba({ ...platba, castka: e.target.value }), "data-testid": "platba-castka" }} />
          <div className="w-[130px]">
            <span className="label-up text-muted block mb-1.5">Způsob</span>
            <Vyber className="field h-9 text-[13px]" value={platba.typ} onChange={(e) => setPlatba({ ...platba, typ: e.target.value })}>
              <option value="BANKA">Bankou</option><option value="HOTOVE">Hotově</option><option value="KARTA">Kartou</option>
            </Vyber>
          </div>
          <Button onClick={addPlatba} disabled={saving} data-testid="add-platba"><Plus size={15} /> Přidat platbu</Button>
        </div>
        {/* Doplatek se nejčastěji platí celý — ať se nepřepisuje z hlavy. */}
        {!uhrazeno && !preplatek && (
          <button type="button" onClick={() => setPlatba((p) => ({ ...p, castka: String(zbyva) }))}
            data-testid="platba-doplatek"
            className="tap mt-2 pill border border-line-strong px-3 h-9 t-meta font-600 text-sec hover:bg-inputbg">
            Doplatit zbytek — <span className="amount font-700 text-ink">{fmtKc(zbyva)} Kč</span>
          </button>
        )}
        {err && <div className="text-[12.5px] text-danger mt-2">{err}</div>}
      </div>
      </>)}
      {neplatiSe && err && <div className="t-meta text-danger mt-2">{err}</div>}
    </div>
  );
}
